import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Music, LayoutDashboard, Settings } from 'lucide-react';
import { getHostTenantSlug } from '../../lib/hostTenant';

const Home = () => {
  const { t } = useTranslation();

  // On a tenant-dedicated subdomain (e.g. tcn.noirock.app) there is no public
  // landing / creator portal yet — go straight to the auth page (TCN-branded
  // via the subdomain theme). Apex noirock.app keeps the multi-space landing.
  // The public install page for the native apps lives at /download-app
  // (registered in Routes.jsx) — not on '/' so returning managers still get
  // the 1-click path to login.
  if (getHostTenantSlug() === 'tcn') {
    return <Navigate to="/login-authentication" replace />;
  }

  const spaces = [
    {
      title: t('home.creatorSpace'),
      description: t('home.creatorSpaceDesc'),
      icon: Music,
      to: '/my/login',
    },
    {
      title: t('home.managerSpace'),
      description: t('home.managerSpaceDesc'),
      icon: LayoutDashboard,
      to: '/login-authentication',
    },
    {
      title: t('home.adminSpace'),
      description: t('home.adminSpaceDesc'),
      icon: Settings,
      to: '/login-authentication',
    },
  ];
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(ellipse, #E5202A 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2">
          <img src="/logo-noirock.png" alt="NoiRock" className="w-7 h-7 rounded-lg" />
          <span className="text-white/40 text-xs">NoiRock</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-3xl text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src="/logo-noirock-square.png" alt="NoiRock" className="w-12 h-12 rounded-xl shadow-lg shadow-red-500/30" />
            <h1
              className="text-4xl md:text-5xl font-bold text-white tracking-tight"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              NoiRock
            </h1>
          </div>

          <p
            className="text-white/60 text-lg md:text-xl mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('home.tagline')}
          </p>

          <p className="text-white/40 text-sm md:text-base leading-relaxed max-w-xl mx-auto mb-12">
            {t('home.heroSubtitle')}
          </p>

          {/* About / TikTok description */}
          <div
            className="rounded-2xl p-6 border border-white/8 text-left max-w-2xl mx-auto mb-12"
            style={{ backgroundColor: '#111111' }}
          >
            <h2
              className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {t('home.aboutTitle')}
            </h2>
            <p className="text-white/40 text-sm leading-relaxed">
              NoiRock is a platform built by NoiRock for TikTok LIVE creators. We use Login Kit to let creators sign in with their TikTok account on our platform (noirock.app). When a creator clicks "Sign in with TikTok", they are redirected to TikTok's authorization page. After granting consent, we retrieve their open_id, display_name, avatar (user.info.basic), username (user.info.profile), and follower/video stats (user.info.stats). We verify the creator exists in our database, then display their personalized dashboard with LIVE performance data.
            </p>
          </div>

          {/* 3 Spaces */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-16">
            {spaces.map((space) => (
              <Link
                key={space.title}
                to={space.to}
                className="rounded-2xl p-6 border border-white/8 text-left transition-all duration-200 hover:border-[#E5202A]/30 hover:shadow-lg hover:shadow-red-500/5 group no-underline"
                style={{ backgroundColor: '#111111' }}
              >
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-4 group-hover:bg-[#E5202A]/10 transition-colors">
                  <space.icon className="w-5 h-5 text-white/40 group-hover:text-[#E5202A] transition-colors" />
                </div>
                <h3
                  className="text-white font-semibold text-base mb-2"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  {space.title}
                </h3>
                <p className="text-white/40 text-sm leading-relaxed">
                  {space.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 px-6 py-6 flex flex-col items-center gap-3">
        <div className="flex items-center gap-4">
          <Link to="/terms" className="text-white/20 text-xs hover:text-white/40 transition-colors no-underline">
            {t('home.termsOfService')}
          </Link>
          <span className="text-white/10 text-xs">·</span>
          <Link to="/privacy" className="text-white/20 text-xs hover:text-white/40 transition-colors no-underline">
            {t('home.privacyPolicy')}
          </Link>
        </div>
        <p className="text-white/15 text-xs">
          © 2026 NoiRock
        </p>
      </div>
    </div>
  );
};

export default Home;
